import type { FC } from "react";
import { useQuery } from "@tanstack/react-query";
import { LinearProgress, Stack, Typography } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { assistantBudgetQueryOptions } from "../../api/assistant";

export const AssistantBudgetPanel: FC = () => {
  const { t, formatDate } = useTranslation();
  const budgetQuery = useQuery(assistantBudgetQueryOptions());

  if (!budgetQuery.data) return null;

  const { spentPercent, weightedTokensSpent, weightedTokenLimit, resetsAt } = budgetQuery.data;
  const left = Math.max(0, weightedTokenLimit - weightedTokensSpent);

  return (
    <Stack sx={{ gap: 0.75, px: 3, py: 2, borderTop: "1px solid", borderColor: theme => theme.palette.divider }}>
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <Typography
          sx={{
            fontSize: theme => theme.typography.pxToRem(12),
            fontWeight: 600,
            color: theme => theme.palette.text.tertiary,
          }}
        >
          {t("Assistant.Budget.Title")}
        </Typography>
        <Typography
          sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.tertiary }}
        >
          {spentPercent} %
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, spentPercent)}
        color={spentPercent >= 100 ? "error" : spentPercent >= 80 ? "warning" : "primary"}
        sx={{ height: 6, borderRadius: 3 }}
      />
      <Typography
        sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.secondary }}
      >
        {t("Assistant.Budget.Left", { left: compact(left), limit: compact(weightedTokenLimit) })}
      </Typography>
      <Typography sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.tertiary }}>
        {t("Assistant.Budget.Resets", { resetsAt: formatDate(resetsAt) })}
      </Typography>
    </Stack>
  );
};

const compact = (tokens: number) =>
  new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(tokens);
